import { Alert, Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import FinancialReportDownloadButton from 'components/v2/common/financialReportDownloadButton';

import { AdminCompaniesResponse } from 'api/rest/analytics';
import { getCompanies } from 'api/rest/companies';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';

const fieldSx = {
    width: '100%',
    minWidth: 0,
    '& .MuiInputLabel-root': {
        color: 'text.secondary',
        fontWeight: 500,
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        backgroundColor: 'background.paper',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '& fieldset': {
            borderColor: 'divider',
        },
        '&:hover fieldset': {
            borderColor: 'primary.light',
        },
        '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.12)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: '4px',
    },
};

const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) {
        return error.message;
    }

    return fallback;
};

interface AdminReportDownloadProps {
    fromDate: Date | null;
    toDate: Date | null;
    serviceProviderId: string;
    hasValidDateRange: boolean;
    onServiceProviderIdChange: (id: string) => void;
    showDownloadButton?: boolean;
    showProviderSelector?: boolean;
}

const AdminReportDownload = ({
    fromDate,
    toDate,
    serviceProviderId,
    hasValidDateRange,
    onServiceProviderIdChange,
    showDownloadButton = true,
    showProviderSelector = true,
}: AdminReportDownloadProps) => {
    const { role } = useAuth();
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const [companies, setCompanies] = useState<AdminCompaniesResponse>({});
    const [providersLoading, setProvidersLoading] = useState(false);
    const [providersError, setProvidersError] = useState('');

    useEffect(() => {
        if (!isSuperSuperAdmin || !showProviderSelector) return undefined;

        let cancelled = false;
        setProvidersLoading(true);
        setProvidersError('');

        getCompanies()
            .then((response) => {
                if (cancelled) return;
                setCompanies(response.data as AdminCompaniesResponse);
            })
            .catch((error) => {
                if (cancelled) return;
                setProvidersError(
                    getErrorMessage(error, 'Failed to load service providers. Please try again.')
                );
            })
            .finally(() => {
                if (!cancelled) setProvidersLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isSuperSuperAdmin, showProviderSelector]);

    const providerOptions = useMemo(
        () =>
            Object.entries(companies).map(([id, name]) => ({
                id,
                label: name,
            })),
        [companies]
    );

    if (!isSuperSuperAdmin && !showDownloadButton) {
        return null;
    }

    const filterColumns =
        isSuperSuperAdmin && showProviderSelector && showDownloadButton
            ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
            : { xs: '1fr' };

    return (
        <Stack spacing={1} sx={{ width: '100%', minWidth: 0 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: filterColumns,
                    gap: 1.25,
                    width: '100%',
                    minWidth: 0,
                    alignItems: 'flex-start',
                }}
            >
                {isSuperSuperAdmin && showProviderSelector ? (
                    <Autocomplete<{ id: string; label: string }, false, false, false>
                        options={providerOptions}
                        value={
                            serviceProviderId
                                ? {
                                      id: serviceProviderId,
                                      label: companies[serviceProviderId] ?? '',
                                  }
                                : null
                        }
                        onChange={(_, option) => onServiceProviderIdChange(option ? option.id : '')}
                        isOptionEqualToValue={(option, val) => option.id === val.id}
                        disabled={providersLoading || Boolean(providersError)}
                        size="small"
                        fullWidth
                        sx={{ width: '100%', minWidth: 0 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Service Provider"
                                placeholder={providersLoading ? 'Loading service providers...' : ''}
                                sx={fieldSx}
                            />
                        )}
                    />
                ) : null}

                {showDownloadButton ? (
                    <FinancialReportDownloadButton
                        serviceProviderId={serviceProviderId}
                        fromDate={fromDate}
                        toDate={toDate}
                        hasValidDateRange={
                            hasValidDateRange && !providersLoading && !providersError
                        }
                        fullWidth
                    />
                ) : null}
            </Box>

            {showDownloadButton && !isSuperSuperAdmin && !serviceProviderId ? (
                <Typography color="error" variant="caption">
                    Your user is missing a service provider ID.
                </Typography>
            ) : null}

            {showProviderSelector && providersError ? (
                <Alert severity="error" sx={{ py: 0 }}>
                    {providersError}
                </Alert>
            ) : null}
        </Stack>
    );
};

export default AdminReportDownload;
